// Live's caption Animation list (#264 part 2, spec docs/specs/editor/captions.md): the theme
// editor's 18 entries besides None, each an entrance by scope, its time and curve, and for some a
// pose while the word is spoken, as live's caption-animations table defines them (read for
// behaviour, never copied). Line scope is word scope without the spoken pose; page scope differs
// where live's does. Two do as their names say where live's do not (spec decision 27): Slide up
// rises from below (live's drops from above), and Random gives each word one of the others (live's
// is one fixed entrance). Pure.
import type { CaptionAnimationPreset, CaptionEasing, CaptionScope } from "../../../api/generated/model";
import type { CaptionActive, CaptionAnimation, CaptionMotion } from "./caption-style";

interface Entry {
  label: string;
  from: Partial<CaptionMotion>;
  durationMs: number;
  easing: CaptionEasing;
  /** The spoken word's pose, at word scope only. */
  active?: CaptionActive;
  /** What page scope changes. */
  page?: { from?: Partial<CaptionMotion>; durationMs?: number };
}

/** Live's list, in its order. */
export const CAPTION_ANIMATIONS: Readonly<Record<CaptionAnimationPreset, Entry>> = {
  immediate: { label: "Immediate", from: { opacity: 1 }, durationMs: 0, easing: "linear" },
  opacity: { label: "Opacity", from: { opacity: 0 }, durationMs: 140, easing: "ease_out_quad", page: { durationMs: 220 } },
  pop_in: {
    label: "Pop in",
    from: { opacity: 0, scale: 0.2 },
    durationMs: 150,
    easing: "ease_out_back",
    active: { scale: 1.15, y: 0, dynamic: true },
    page: { durationMs: 250 },
  },
  slide_in: {
    label: "Slide in",
    from: { opacity: 0, y: 30 },
    durationMs: 160,
    easing: "ease_out_cubic",
    page: { from: { opacity: 0, y: 60 }, durationMs: 250 },
  },
  lateral_slide_left: {
    label: "Lateral slide left",
    from: { opacity: 0, x: -40 },
    durationMs: 180,
    easing: "ease_out_cubic",
    page: { from: { opacity: 0, x: -80 } },
  },
  lateral_slide_right: {
    label: "Lateral slide right",
    from: { opacity: 0, x: 40 },
    durationMs: 180,
    easing: "ease_out_cubic",
    page: { from: { opacity: 0, x: 80 } },
  },
  flip_in: { label: "Flip in", from: { opacity: 0, y: -15, rotation: -20, scale: 0.8 }, durationMs: 180, easing: "ease_out_back" },
  scale_in: { label: "Scale in", from: { opacity: 0, scale: 0.4 }, durationMs: 160, easing: "ease_out_quad" },
  impact_scale: {
    label: "Impact scale",
    from: { opacity: 0, scale: 1.8 },
    durationMs: 180,
    easing: "ease_out_back",
    active: { scale: 1.2, y: 0, dynamic: true },
  },
  slide_up: {
    label: "Slide up",
    from: { opacity: 0, y: 25 },
    durationMs: 160,
    easing: "ease_out_quad",
    page: { from: { opacity: 0, y: 50 } },
  },
  pop_in_slide_up: { label: "Pop in slide up", from: { opacity: 0, y: 25, scale: 0.5 }, durationMs: 180, easing: "ease_out_back" },
  transform: { label: "Transform", from: { opacity: 0, rotation: 30, scale: 0.6 }, durationMs: 180, easing: "ease_out_cubic" },
  jump: {
    label: "Jump",
    from: { opacity: 0, y: -30 },
    durationMs: 200,
    easing: "ease_out_bounce",
    active: { scale: 1, y: -8, dynamic: true },
  },
  zoom: { label: "Zoom", from: { opacity: 0, scale: 1.5 }, durationMs: 180, easing: "ease_out_cubic" },
  soft_rise: { label: "Soft rise", from: { opacity: 0, y: 15 }, durationMs: 220, easing: "ease_out_sine" },
  staggered_pop: { label: "Staggered pop", from: { opacity: 0, scale: 0.1 }, durationMs: 200, easing: "ease_out_back" },
  rapid_stream: { label: "Rapid stream", from: { opacity: 0, x: -15 }, durationMs: 80, easing: "linear" },
  random: { label: "Random", from: { opacity: 0, scale: 0.5, rotation: -10 }, durationMs: 180, easing: "ease_out_back" },
};

const REST: CaptionMotion = { opacity: 1, scale: 1, x: 0, y: 0, rotation: 0 };

/** An entry of live's list at a scope, as the look keeps it. */
export function captionAnimationOf(preset: CaptionAnimationPreset, scope: CaptionScope): CaptionAnimation {
  const entry = CAPTION_ANIMATIONS[preset];
  const page = scope === "page" ? entry.page : undefined;
  return {
    preset,
    scope,
    enterFrom: { ...REST, ...(page?.from ?? entry.from) },
    enterTo: REST,
    durationMs: page?.durationMs ?? entry.durationMs,
    easing: entry.easing,
    active: scope === "word" ? (entry.active ?? null) : null,
  };
}

/** The entrances Random draws from: every other but Immediate. */
const DRAWN: readonly CaptionAnimationPreset[] = (Object.keys(CAPTION_ANIMATIONS) as CaptionAnimationPreset[]).filter(
  (preset) => preset !== "immediate" && preset !== "random",
);

/**
 * The animation a word enters by: the look's, or for Random one of the others, the same for the
 * same word every time (drawn from its place in the caption and its text).
 */
export function wordAnimation(animation: CaptionAnimation | null, index: number, text: string): CaptionAnimation | null {
  if (animation?.preset !== "random") return animation;
  let seed = index + 1;
  for (const char of text) seed = (seed * 31 + char.charCodeAt(0)) >>> 0;
  return captionAnimationOf(DRAWN[seed % DRAWN.length]!, animation.scope);
}
